import React, { useMemo } from "react";

import {
  Avatar,
  Badge,
  Box,
  ListItemAvatar,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import NavLink from "@/components/NavLink";
import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";
import { apply } from "@/utils/functions";
import { parameterize } from "@/utils/string";

interface Props {
  chatId: string;
}

const useStyles = makeStyles()((theme) => ({
  active: {
    backgroundColor: "#EDF0F5",
  },
  primaryText: {
    fontSize: 18,
    lineHeight: 1,
    color: colors.dark,
  },
  secondaryText: {
    marginTop: theme.spacing(2),
    fontSize: 14,
    lineHeight: 1,
    color: colors.gray,
  },
  time: {
    fontSize: 14,
    lineHeight: 1,
    color: colors.gray,
  },
  secondary: {
    pointerEvents: "none",
    paddingRight: 8,
  },
}));

const messageListItemFragment = graphql(`
  fragment MessageListItem on Chat {
    id
    client {
      id
      profile {
        id
        fullName
        avatarUrl(variant: THUMB)
      }
    }
    viewerUnreadMessagesCount
    latestMessage {
      id
      channel
      body
      attachments {
        id
      }
      readAt
      createdAt
      viewerIsSender
    }
  }
`);

const MessageListItem: React.FC<Props> = ({ chatId }) => {
  const { classes } = useStyles();

  const [chat] = useFragment(messageListItemFragment, `Chat:${chatId}`);

  const renderLink = useMemo(() => {
    const to = `/messages/${chatId}/${apply(
      chat?.latestMessage?.channel,
      parameterize
    )}`;
    const Link = React.forwardRef<HTMLAnchorElement>((itemProps, ref) => (
      <NavLink
        to={to}
        ref={ref}
        {...itemProps}
        activeClassName={classes.active}
      />
    ));
    Link.displayName = "Link";
    return Link;
  }, [chatId, chat?.latestMessage?.channel]);

  if (chat == null) return null;

  let latestMessageBody = "No conversation yet. Message now!";

  if (chat.latestMessage != null) {
    const senderName = chat.latestMessage.viewerIsSender
      ? "You"
      : chat.client?.profile?.fullName;
    latestMessageBody =
      chat.latestMessage.body || `${senderName} sent an attachment.`;
  }

  return (
    <ListItemButton component={renderLink}>
      <ListItemAvatar>
        <Avatar
          alt={chat.client?.profile?.fullName}
          src={chat.client?.profile?.avatarUrl ?? undefined}
        />
      </ListItemAvatar>
      <ListItemText
        classes={{
          primary: classes.primaryText,
          secondary: classes.secondaryText,
        }}
        primaryTypographyProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            marginRight: "64px",
          },
        }}
        secondaryTypographyProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            marginRight: "64px",
          },
        }}
        primary={chat.client?.profile?.fullName}
        secondary={latestMessageBody}
      />
      <ListItemSecondaryAction className={classes.secondary}>
        <Typography className={classes.time}>
          {apply(chat.latestMessage?.createdAt, (datetime) =>
            format({ datetime: datetime }, "p")
          )}
        </Typography>
        <Box marginBottom={3} />
        <Box display="flex" justifyContent="flex-end" marginRight={5}>
          <Badge
            badgeContent={chat.viewerUnreadMessagesCount}
            color="primary"
          />
        </Box>
      </ListItemSecondaryAction>
    </ListItemButton>
  );
};

export default MessageListItem;
